import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { checkAuth, logout } from '../auth';

// 컴포넌트 스타일 적용
import '../css/Main.css';

// 메인 프레임에 보여지는 컴포넌트
import AicaFrame from './AicaFrame';  // AICA 페이지
import AiccFrame from './AiccFrame';  // AICC 페이지
import PatientListFrame from './PatientListFrame';  // 환자 목록 페이지 
import LensRecommendFrame from './LensRecommendFrame'; // 렌즈 추천 페이지 (기존)
import LensRecommendV2Frame from './lensRecommend/LensRecommendV2Frame'; // 렌즈 추천 v2

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const LAST_ACTIVE_FRAME_KEY = 'lastActiveFrame';

const canAccessFrame = (frame, userData) => {
  const userUrl = userData?.url?.toLowerCase();

  if (!frame) return false;
  if (frame === userData?.url) return true;
  if (frame === 'lensRecommend') return userUrl === 'aida';
  if (frame === 'lensRecommendV2') return userUrl === 'aida';
  if (frame === 'patientList') return true;

  return ['aicc', 'aica', 'aida'].includes(frame);
};

const getInitialFrame = (userData) => {
  const lastActiveFrame = sessionStorage.getItem(LAST_ACTIVE_FRAME_KEY);

  return canAccessFrame(lastActiveFrame, userData) ? lastActiveFrame : userData.url;
};

const Main = () => {
  /* ------------------------ State ------------------------ */
  const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5분마다 세션 체크 

  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  //const [userNo, setUserNo] = useState(null);

  const [activeFrame, setActiveFrame] = useState(null); // 현재 활성화된 프레임
  
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);



  /* ------------------------ Handler ------------------------ */
  // 프레임 변경 함수
  const handleFrameChange = (frame) => {
    setActiveFrame(frame);
    sessionStorage.setItem(LAST_ACTIVE_FRAME_KEY, frame);
  }


  // 외부마이크 디바이스 조회
  const getDeviceList = async () => {
    const audioDevices = await getAudioDevices();
    setDeviceList(audioDevices);
  };


  // 로그아웃 함수 (메모이제이션)
  const handleLogout = useCallback(async () => {
    console.log('[Main] Call Logout Function');
    
    try {
      //await logout();

      // 세션 저장소 사용자 정보 초기화
      sessionStorage.removeItem('user');
      sessionStorage.removeItem(LAST_ACTIVE_FRAME_KEY);
  
      // 로컬 저장소 자동 로그인 초기화
      localStorage.removeItem('autoLogin');
      localStorage.removeItem('autoLoginState');

      // 상태 변수 초기화
      setUser(null);
      setActiveFrame(null);
    } catch (error) {
      console.error('[Main] Logout Error ::: ', error);
    } finally {
      //navigate('/login', { replace: true });
      window.location.href = '/login';
    }    
  }, []);
  


  /* ------------------------ useEffect ------------------------ */
  /*
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // 로그인 결과 조회
        if (location.state) {
          setUser(location.state.user);
          setUserNo(location.state.user.userNo);
          setActiveFrame(location.state.user.url);
          console.log('[Main.jsx] 로그인 결과 ::: ', location.state.user);
        }

        // TODO 상담실(AICA) 권한 사용자가 접근 시 외부마이크 디바이스 조회
        if (location.state.user.url === 'aica') {
          getDeviceList();
        }
      } catch (error) {
        console.error('[Main.jsx] 컴포넌트 초기화 실패 ::: ', error);
        //localStorage.removeItem('user');
        navigate('/', {replace: true});
      }
    };

    initializeComponent();
  }, [location]);
  */

  // 세션 체크 인터벌
  useEffect(() => {
    const sessionCheck = setInterval(() => {
      const userData = checkAuth();
      if (!userData) {
        handleLogout();
      }
    }, SESSION_CHECK_INTERVAL); // 5분마다 체크

    return () => clearInterval(sessionCheck);
  }, []);

  

  // 사용자 정보 로드
  useEffect(() => {
    const userData = checkAuth();

    if (!userData) {
      navigate('/', {replace: true});
      return;
    }

    setUser(userData);
    setActiveFrame(getInitialFrame(userData));
  }, [navigate]);


  



  // 사용자 정보가 없으면 렌더링하지 않음
  if (!user) return null;



  
  /* ------------------------ 화면 렌더링 ------------------------ */  
  return (
    <div className="main-container">
      
      {/* 왼쪽 네비게이션 바 */}
      <nav className="nav-bar">
        
        {/* AICA 로고 */}
        <div className="nav-logo">
          <i className="icon-logo icon-img-aica-logo" />
        </div>

        <div className="nav-menu">
          <button
            type="button"
            className={`nav-text-btn${activeFrame === user.url ? ' active' : ''}`}
            onClick={() => handleFrameChange(user.url)}
          >
            진료/상담
          </button>

          {/* 렌즈 추천 - 진료실(AIDA) 권한 계정만 표시 */}
          {user.url?.toLowerCase() === 'aida' && (
            <>
              <button
                type="button"
                className={`nav-text-btn${activeFrame === 'lensRecommendV2' ? ' active' : ''}`}
                title="렌즈 추천"
                onClick={() => handleFrameChange('lensRecommendV2')}
              >
                ICL
              </button>
              {/* <button
                type="button"
                className={`nav-text-btn nav-text-btn-sub${activeFrame === 'lensRecommend' ? ' active' : ''}`}
                title="렌즈 추천 (기존)"
                onClick={() => handleFrameChange('lensRecommend')}
              >
                ICL 기존
              </button> */}
            </>
          )}
        </div>

        {/* 로그아웃 버튼 */}
        <div className="spacer" />
        <button className="logout-button" onClick={handleLogout}>
          로그아웃
        </button>
      </nav>


      {/* 메인 프레임 영역 */}
      <div className="main-frame">
        {/* activeFrame이 null이면 아무것도 렌더링하지 않음 */}
        {activeFrame && ( 
          activeFrame === 'patientList' ? (
            <PatientListFrame />
          ) : activeFrame === 'lensRecommend' ? (
            <LensRecommendFrame />
          ) : activeFrame === 'lensRecommendV2' ? (
            <LensRecommendV2Frame />
          ) : activeFrame === 'aicc' ? (
            <AiccFrame />
          ) : (
            <AicaFrame />
          )
        )}
      </div>
    </div>
  );

};
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 메인 컴포넌트
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default Main;


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 마이크 권한 체크
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const checkPermissions = async () => {
  try {
    const result = await navigator.permissions.query({ name: 'microphone' });
    console.log('Microphone permission:', result.state);
    // 'granted', 'denied', 'prompt' 중 하나 반환
  } catch (error) {
    console.error('Permission check failed:', error);
  }
};


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 외부마이크 디바이스 조회
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const getAudioDevices = async () => {
  try {
    // 브라우저 호환성 체크를 위한 방어코드
    if (!navigator.mediaDevices?.getUserMedia) {
      checkPermissions();
      
      navigator.mediaDevices = {};
      navigator.mediaDevices.getUserMedia = function(constraints) {
        const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
        console.log('getUserMedia 함수 호출 ::: ', getUserMedia);

        if (!getUserMedia) {
          console.log('브라우저 호환성 체크 실패 ::: getUserMedia is not implemented in this browser');
          return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
        }

        return new Promise((resolve, reject) => {
          console.log('브라우저 호환성 체크 성공 ::: getUserMedia is implemented in this browser');
          getUserMedia.call(navigator, constraints, resolve, reject);
        });
      }
    }

    // 권한 상태 확인
    const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
    console.log('마이크 권한 상태:', permissionStatus.state);
    
    // 권한이 거부된 경우
    if (permissionStatus.state === 'denied') {
      alert('마이크 사용 권한이 거부되었습니다. 브라우저 설정에서 권한을 허용해주세요.');
      return;
    }
    
    // 미디어 장치 접근권한 요청
    await navigator.mediaDevices.getUserMedia({ audio: true })
      .catch(error => {
        console.log('마이크 권한이 거부됨:', error);
        throw new Error('마이크 접근 권한이 필요합니다.');
      });

    // 사용 가능한 모든 디바이스 조회
    const devices = await navigator.mediaDevices.enumerateDevices();

    // 오디오 입력 장치(마이크) 필터링
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
    console.log('사용 가능한 마이크 목록:', audioInputDevices);

    return audioInputDevices;
  } catch (error) {
    console.error('마이크 디바이스 조회 실패:', error);
    return [];
  }
};


